import moment from 'moment';
import get from 'lodash/get';
import { camelizeKeys } from 'humps';
import isEmpty from 'lodash/isEmpty';

import { S3_PRODUCT_BASE_URL } from 'utils/constants';

const PENDING_CUSTOMER = 'pending_customer';
const PENDING_MANAGEMENT = 'pending_management';
const ORDERED = 'ordered';
const SHIPPED = 'shipped';
const DELIVERED = 'delivered';

const ORDER_STATUS_COPY = {
  pending_customer: 'Pending Customer Approval',
  pending_management: 'Pending Modsy Review',
  ordered: 'Ordered',
  shipped: 'Shipped',
  delivered: 'Delivered',
  returned: 'Returned',
  in_home_delivery: 'In Home Delivery',
  cancelled: 'Cancelled',
  exchanged: 'Exchanged'
};

const SHIPPING_TYPE_COPY = {
  unknown: 'Unknown',
  inhome: 'In-Home Delivery',
  threshold: 'Threshold Delivery',
  inhomeassembly: 'In-Home + Assembly',
  fedexground: 'FedEx Ground',
  fedexexpress: 'FedEx Express',
  basicfreight: 'Basic Freight Delivery',
  upsground: 'UPS Ground',
  upsexpress: 'UPS Express',
  usps: 'USPS',
  whiteglove: 'White Glove Delivery',
  instorepickup: 'In-Store Pick Up',
  warehousepickup: 'Warehouse Pick Up',
  standardground: 'Standard Ground',
  groundleveldelivery: 'Ground Level Delivery',
  fedexoversize: 'FedEx Oversize',
  frontdoorltl: 'Front Door LTL',
  flatrate: 'Flate Rate Delivery',
  other: 'Other - please contact us for details on delivery methos'
};

const INVENTORY_STATUS_COPY = {
  in_stock: 'In Stock',
  out_of_stock: 'Currently Unavailable',
  backordered: 'Backordered',
  discontinued: 'Discontinued',
  canceled: 'Canceled'
};

class OrderItem {
  static createOrderItemFromWarehouseResponse ({ whOrderItem }) {
    const whOrderItemCamelized = camelizeKeys(whOrderItem);

    return new OrderItem({
      id: whOrderItemCamelized.id,
      addtlShipmentInfo: whOrderItemCamelized.addtlShipmentInfo,
      deliveryEndDate: whOrderItemCamelized.retailerShippingEndDate,
      deliveryStartDate: whOrderItemCamelized.retailerShippingStartDate,
      deliveryStatus: whOrderItemCamelized.deliveryStatus,
      inventoryStatus: whOrderItemCamelized.inventoryStatus,
      itemNameAtOrderCreation: whOrderItemCamelized.itemNameAtOrderCreation,
      modsyDiscount: whOrderItemCamelized.discount,
      product: whOrderItemCamelized.product || {},
      productId: get(whOrderItemCamelized, 'product.id'),
      quantity: whOrderItemCamelized.quantity,
      retailerDiscount: whOrderItemCamelized.retailerDiscount,
      retailerOrderNumber: whOrderItemCamelized.retailerOrderNumber,
      salesPrice: whOrderItemCamelized.salesPrice,
      shipments: whOrderItemCamelized.shipments,
      shippingType: whOrderItemCamelized.retailerShippingType,
      trackingNumber: whOrderItemCamelized.retailerTrackingNumber
    });
  }

  /** Creates a new order item instance with optional overrides. */
  constructor (overrides) {
    this.fields = { ...overrides };
  }

  /** Creates a new design copied from this with optional overrides. */
  makeCopy (overrides) {
    return new OrderItem({ ...this.fields, ...overrides });
  }

  formatCurrency (amount) {
    return amount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    });
  }

  formatDate (date) {
    return moment(date).format('MM/DD/YY');
  }

  // =======
  // GETTERS
  // =======
  /** Returns the id for this order. */
  id () { return this.fields.id; }

  cancelledItem () { return this.quantity() === 0; }

  deliveryRange () {
    const dateArray = [];
    const startDate = this.deliveryStartDate();
    const endDate = this.deliveryEndDate();

    if (startDate) {
      dateArray.push(this.formatDate(startDate));
    }

    if (endDate) {
      dateArray.push(this.formatDate(endDate));
    }

    return isEmpty(dateArray) ? null : dateArray.join(' - ');
  }

  addtlShipmentInfo () { return this.fields.addtlShipmentInfo; }

  deliveryEndDate () { return this.fields.deliveryEndDate; }

  deliveryStartDate () { return this.fields.deliveryStartDate; }

  hasRetailerDiscount () { return this.retailerDiscount() < 0; }

  hasShippingInfo () {
    return !this.isPending() &&
      (this.isOrdered() || this.isShipped() || this.isDelivered());
  }

  hasTrackingInfo () {
    return (this.isShipped() || this.isDelivered()) && this.trackingNumber();
  }

  itemNameAtOrderCreation () { return this.fields.itemNameAtOrderCreation || this.name(); }

  inventoryStatus () { return this.fields.inventoryStatus; }

  inventoryStatusText () { return INVENTORY_STATUS_COPY[this.inventoryStatus()]; }

  image () { return get(this.fields, 'product.image'); }

  imageUrl () { return this.image() ? `${S3_PRODUCT_BASE_URL}${this.image()}` : null; }

  isDelivered () { return DELIVERED === this.orderStatus(); }

  isOrdered () { return ORDERED === this.orderStatus(); }

  isPending () { return [PENDING_CUSTOMER, PENDING_MANAGEMENT].includes(this.orderStatus()); }

  isShipped () { return SHIPPED === this.orderStatus(); }

  modsyDiscount () { return -parseFloat(this.fields.modsyDiscount); }

  name () { return this.fields.product.name; }

  productId () { return this.fields.productId; }

  quantity () { return this.fields.quantity; }

  retailerDiscount () { return -parseFloat(this.fields.retailerDiscount); }

  retailerOrderNumber () { return this.fields.retailerOrderNumber; }

  salesPrice () { return parseFloat(this.fields.salesPrice); }

  salesTotal () {
    return parseFloat(this.salesPrice() + this.retailerDiscount()) * parseFloat(this.quantity());
  }

  shipments () { return this.fields.shipments; }

  shippingType () { return this.fields.shippingType; }

  shippingTypeCopy () { return SHIPPING_TYPE_COPY[this.shippingType()]; }

  orderStatus () { return this.fields.deliveryStatus; }

  orderStatusText () {
    switch (true) {
      case this.isOrdered():
        return 'Ordered';
      case this.isShipped():
        return 'Shipped';
      case this.isDelivered():
        return 'Delivered';
      default:
        return ORDER_STATUS_COPY[this.orderStatus()];
    }
  }

  trackingNumber () { return this.fields.trackingNumber; }
}

export default OrderItem;
